import React, { useState, useContext } from 'react';
import Joi from 'joi-browser';
import { toast } from 'react-toastify';
import { useLanguage } from '../../i18n';
import { submitLead, CONTACT_PHONE } from '../../utils/leads';
import './style.scss';

const Form = () => {
    const { t, language } = useLanguage();

    const [formState, setFormState] = useState({
        name: '',
        phone: '',
        email: '',
        country: '',
        message: '',
        error: {}
    });

    const schema = {
        email: Joi.string().email({ minDomainAtoms: 2 }).required().error(errors => {
            errors.forEach(err => {
                switch (err.type) {
                    case "string.email":
                        err.message = language === 'hi' ? 'कृपया एक वैध ईमेल दर्ज करें' : 'Email must be a valid email';
                        break;
                    default:
                        err.message = language === 'hi' ? 'ईमेल खाली नहीं हो सकता' : 'Email cannot be empty';
                        break;
                }
            });
            return errors;
        }),
        phone: Joi.string().min(10).max(13).required().error(errors => {
            errors.forEach(err => {
                switch (err.type) {
                    case "string.min":
                        err.message = language === 'hi' ? 'फोन नंबर कम से कम 10 अंक का होना चाहिए' : 'Phone number must be at least 10 characters long';
                        break;
                    case "string.max":
                        err.message = language === 'hi' ? 'फोन नंबर 13 अंक से अधिक नहीं हो सकता' : 'Phone number cannot be longer than 13 characters';
                        break;
                    default:
                        err.message = language === 'hi' ? 'फोन नंबर खाली नहीं हो सकता' : 'Phone cannot be empty';
                        break;
                }
            });
            return errors;
        }),
        name: Joi.string().required().error(errors => {
            errors.forEach(err => {
                err.message = language === 'hi' ? 'नाम खाली नहीं हो सकता' : 'Name cannot be empty';
            });
            return errors;
        }),
        message: Joi.string().min(10).required().error(errors => {
            errors.forEach(err => {
                switch (err.type) {
                    case "string.min":
                        err.message = language === 'hi' ? 'संदेश कम से कम 10 अक्षर का होना चाहिए' : 'Case message must be at least 10 characters long';
                        break;
                    default:
                        err.message = language === 'hi' ? 'संदेश खाली नहीं हो सकता' : 'Case message cannot be empty';
                        break;
                }
            });
            return errors;
        }),
        // The "City" box (stored as `country`) is optional.
        country: Joi.string().allow('').optional(),
    };

    const validationProperty = (name, value) => {
        const Obj = { [name]: value };
        const schemaObj = { [name]: schema[name] }
        const { error } = Joi.validate(Obj, schemaObj);
        return error ? error.details[0].message : null
    };

    const changeHandler = event => {
        const { name, value } = event.target;
        const error = { ...formState.error };
        const errorMessage = validationProperty(name, value);
        if (errorMessage) {
            error[name] = errorMessage;
        } else {
            delete error[name];
        }
        setFormState(prev => ({
            ...prev,
            [name]: value,
            error
        }));
    };

    const validate = () => {
        const options = { abortEarly: false }
        const form = {
            name: formState.name,
            email: formState.email,
            phone: formState.phone,
            message: formState.message,
            country: formState.country,
        }
        const { error } = Joi.validate(form, schema, options);
        if (!error) return null;

        const errors = {};
        for (let item of error.details) errors[item.path[0]] = item.message
        return errors;
    };

    const submitHandler = async event => {
        event.preventDefault();
        const error = validate();
        if (error) {
            setFormState(prev => ({ ...prev, error: error || {} }));
            return;
        }
        try {
            await submitLead({
                name: formState.name,
                phone: formState.phone,
                email: formState.email,
                city: formState.country,
                message: formState.message,
            }, 'Contact form');
            setFormState({
                name: '',
                phone: '',
                email: '',
                country: '',
                message: '',
                error: {}
            });
            toast.success(language === 'hi' ? 'आपका संदेश सफलतापूर्वक भेजा गया!' : 'Query submitted successfully!');
        } catch (error) {
            console.error('Lead submission failed:', error);
            toast.error(language === 'hi'
                ? `संदेश नहीं भेजा जा सका। कृपया हमें ${CONTACT_PHONE} पर कॉल करें।`
                : `Couldn't send your query. Please call us on ${CONTACT_PHONE}.`);
        }
    };

    return (
        <form onSubmit={submitHandler} className='contactForm' style={{ background: "#F9F6EE", padding: 'clamp(20px, 5vw, 44px)', borderRadius: '14px', textAlign: "center", boxShadow: "none" }}>
            <div className="row">
                <div className="col-sm-12 col-12">
                    <div className="formInput">
                        <input
                            style={{ background: "white", color: "black" }}
                            placeholder={t('contact.fullName')}
                            value={formState.name}
                            name="name"
                            onChange={changeHandler}
                            className="form-control"
                            type="text" />
                        {formState.error.name && <p style={{ color: "red", fontSize: "12px", marginTop: "2px", textTransform: "None" }}>{formState.error.name}</p>}
                    </div>
                </div>
                <div className="col-sm-12 col-12">
                    <div className="formInput">
                        <input
                            style={{ background: "white" }}
                            placeholder={t('contact.mobileNumber')}
                            value={formState.phone}
                            name="phone"
                            onChange={changeHandler}
                            className="form-control"
                            type="text" />
                        {formState.error.phone && <p style={{ color: "red", fontSize: "12px", marginTop: "2px", textTransform: "None" }}>{formState.error.phone}</p>}
                    </div>
                </div>
                <div className="col-sm-12 col-12">
                    <div className="formInput">
                        <input
                            style={{ background: "white" }}
                            placeholder={t('contact.email')}
                            value={formState.email}
                            name="email"
                            onChange={changeHandler}
                            className="form-control"
                            type="email" />
                        {formState.error.email && <p style={{ color: "red", fontSize: "12px", marginTop: "2px", textTransform: "None" }}>{formState.error.email}</p>}
                    </div>
                </div>
                <div className="col-sm-12 col-12">
                    <div className="formInput">
                        <input
                            style={{ background: "white" }}
                            placeholder={t('contact.city')}
                            value={formState.country}
                            name="country"
                            onChange={changeHandler}
                            className="form-control"
                            type="text" />
                        {formState.error.country && <p style={{ color: "red", fontSize: "12px", marginTop: "2px", textTransform: "None" }}>{formState.error.country}</p>}
                    </div>
                </div>
                <div className="col-sm-12 col-12">
                    <div className="formInput">
                        <input
                            style={{ background: "white" }}
                            placeholder={t('contact.legalIssue')}
                            value={formState.message}
                            name="message"
                            onChange={changeHandler}
                            className="form-control"
                            type="text" />
                        {formState.error.message && <p style={{ color: "red", fontSize: "12px", marginTop: "2px", textTransform: "None" }}>{formState.error.message}</p>}
                    </div>
                </div>
                <div className="col-12">
                    <button type="submit">{t('contact.contactNow')}</button>
                </div>
            </div>
        </form>
    );
};

export default Form;
